import { Button, ButtonSecondary, Skeleton, TextWithIcon, Toast } from '@common-web';
import DragAndDrop from '@common-web/drag-and-drop';
import { MAX_IMAGE_COUNT, MAX_IMAGE_SIZE } from '@constants/generic-constants';
import { strings } from '@constants/strings';
import { getBase64, handleFileUploadChange, handleImageRemove } from '@utils/file-uploader-utils';
import { Flex, Image, message, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import cx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { FaImages } from 'react-icons/fa';
import styles from './index.module.scss';

const ImageUpload = ({
  uploadMultiple = false,
  onChange = () => { },
  attachmentType = 'generic',
  associationKey = 'images',
  value = [],
  conditionToShowUploadBtn = true,
  uploadButtonWithList = false,
  showCustomRender = false,
  setShowMore = () => { },
  showMore,
  customItemRender = () => { },
  isSortable = false,
  uploadBtnText = 'Add Images',
  showDescription = true,
  descriptors = ['Ads with pictures get 5x more views.', 'Double click to set cover image.'],
  loading = false,
  setLoading = () => { },
  disabled = false,
  maxCount = undefined,
  alwaysShowUploadBtn = false,
  dimensions = null,
  uploadMultipleStyles = false,
  cropable = false,
  btnClassName = null,
  uploadsTab = null,
  dimensionText = null,
  height = 88,
  width = 88,
  formats = 'image/*',
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [numLoaders, setNumLoaders] = useState(value?.length);

  const onUploadChange = async ({ file, fileList }) => {
    file = file?.originFileObj ?? file;
    let imageDimensions;
    try {
      imageDimensions = await getImageDimensions(file);
    } catch (_err) { }

    const isFormatValid = validateFile(file);
    if (!isFormatValid) return;

    if (file?.size > MAX_IMAGE_SIZE) {
      setNumLoaders(fileList?.length - 1 > MAX_IMAGE_COUNT ? MAX_IMAGE_COUNT : fileList?.length - 1);
      Toast.error(`${strings?.file} ${file?.name} ${strings?.file_size_too_large_5mb}`);
    } else if (
      dimensions &&
      imageDimensions &&
      Math.abs(dimensions?.width / dimensions?.height - imageDimensions?.width / imageDimensions?.height) > 0.2
    ) {
      setNumLoaders(fileList?.length - 1 > MAX_IMAGE_COUNT ? MAX_IMAGE_COUNT : fileList?.length - 1);
      Toast.error(`${strings?.image_dimensions_must_be} ${dimensions?.width} x ${dimensions?.height}`);
    } else {
      setNumLoaders(fileList?.length > MAX_IMAGE_COUNT ? MAX_IMAGE_COUNT : fileList?.length);
      const index = value?.findIndex((e) => e?.uid === file?.uid);
      if (index > -1) {
        if (fileList?.length < MAX_IMAGE_COUNT) {
          setShowMore(false);
        }
      } else {
        handleFileUploadChange({
          file,
          fileList,
          attachmentType,
          associationKey,
          setter: onChange,
          loadingSetter: setLoading,
        });
      }
    }
  };

  const uploadRef = useRef(null);

  const handleButtonClick = (e) => {
    uploadRef.current?.upload?.uploader?.onClick(e);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  function getImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  const validateFile = useCallback((file) => {
    const allowedFormats = formats ? formats.split(',').map((format) => format.trim().toLowerCase()) : [];
    const fileName = file?.name?.toLowerCase();
    const isFormatValid = allowedFormats.length === 0 || allowedFormats.some((format) => fileName.endsWith(format));

    if (formats !== 'image/*' && !isFormatValid) {
      message.error(`${strings?.file} ${file?.name} has invalid format. Allowed formats: ${formats}`);
      return false;
    }
    return true;
  }, [formats]);

  const dndItems = value?.slice(0, showMore ? value?.length : MAX_IMAGE_COUNT)?.map((image, index) => ({
    ...image,
    content: customItemRender(
      null,
      image,
      null,
      {
        remove: () => {
          handleImageRemove({ file: image, files: value, setter: onChange });
          if (value?.length - 1 < MAX_IMAGE_COUNT) {
            setShowMore(false);
          }
        },
        preview: () => handlePreview(image),
      },
      index === 0
    ),
  }));

  const setImageOrder = (files) => {
    if (value?.length > MAX_IMAGE_COUNT && !showMore) {
      onChange([...files, ...(value ?? []).slice(MAX_IMAGE_COUNT, value?.length)]);
    } else {
      onChange(files);
    }
  };

  const addImageSection = (
    <>
      {value?.length === 0 &&
        dndItems.length < MAX_IMAGE_COUNT &&
        !showMore &&
        loading &&
        [...Array(numLoaders - value?.length).keys()]?.map((key) => (
          <Skeleton key={key} height={height} width={width} />
        ))}
      {(alwaysShowUploadBtn ||
        (value?.length > 0 &&
          (value?.length < maxCount || !maxCount) &&
          uploadMultiple &&
          (value?.length < MAX_IMAGE_COUNT || showMore))) && (
          <ButtonSecondary
            onClick={handleButtonClick}
            icon={loading ? 'LoadingOutlined' : 'MdAdd'}
            iconSize={'24px'}
            type={'dashed-primary-light'}
            style={{
              '--ant-control-height': '88px',
              minWidth: 'var(--ant-control-height)',
              display: 'inline',
            }}
            disabled={loading}
          />
        )}
    </>
  );

  const uploadSection = (
    <Upload
      disabled={disabled}
      name={'file'}
      accept={formats}
      listType={showCustomRender ? `text` : 'picture-card'}
      multiple={uploadMultiple}
      openFileDialogOnClick={false}
      onChange={cropable ? () => { } : onUploadChange}
      beforeUpload={() => {
        return cropable;
      }}
      customRequest={() => { }}
      onPreview={handlePreview}
      onRemove={(file) => {
        handleImageRemove({ file, files: value, setter: onChange });
      }}
      fileList={value}
      itemRender={() => <></>}
      className={cx(styles.imageContainer, styles.imageContainerForSort)}
      rootClassName={styles.imageContainerBase}
      maxCount={maxCount}
      ref={uploadRef}
    >
      {conditionToShowUploadBtn && uploadButtonWithList && (
        <Flex align="center" gap={50}>
          {uploadMultiple || uploadMultipleStyles ? (
            <>
              <Flex vertical gap={8}>
                <Button
                  onClick={handleButtonClick}
                  text={uploadBtnText}
                  type={'primary'}
                  shape={'round'}
                  icon={'FaImages'}
                />
                <div className="fs-12 gray-500" style={{ textAlign: 'center' }}>
                  Maximum file size: 5 MB
                </div>
              </Flex>
              {showDescription && (
                <Flex gap={6} vertical>
                  {descriptors.map((description) => (
                    <TextWithIcon key={description} icon="FaCheck" iconColor="#00A651" text={description} />
                  ))}
                </Flex>
              )}
            </>
          ) : (
            !loading && (
              <Flex
                onClick={handleButtonClick}
                align="center"
                gap={4}
                className={btnClassName ? styles[btnClassName] : styles.uploadImgBtn}
              >
                {uploadsTab ? (
                  <FaImages size={32} color="var(--ant-blue-4)" />
                ) : (
                  <FaImages size={20} color="var(--ant-color-primary)" />
                )}
                <span className="fw-600">{uploadsTab ? 'Upload Image' : uploadBtnText}</span>
                {dimensionText && <span style={{ color: 'var(--gray-500' }}>{dimensionText}</span>}
              </Flex>
            )
          )}
        </Flex>
      )}
      {(alwaysShowUploadBtn || value?.length || loading) && (
        <Flex wrap gap={12}>
          {isSortable ? (
            <DragAndDrop
              loading={loading}
              showUploadBtn={!uploadButtonWithList}
              vertical={false}
              dragItems={dndItems}
              itemOrder={dndItems}
              setItemOrder={setImageOrder}
            >
              {addImageSection}
            </DragAndDrop>
          ) : (
            <>
              {dndItems?.map((image) => (
                <React.Fragment key={image.uid || image.id}>{image?.content}</React.Fragment>
              ))}
              {addImageSection}
            </>
          )}
        </Flex>
      )}
    </Upload>
  );

  return (
    <>
      {cropable ? (
        <ImgCrop
          aspect={dimensions ? dimensions?.width / dimensions?.height : undefined}
          onModalOk={(file) => {
            onUploadChange({ file, fileList: [...value, file] });
          }}
          beforeCrop={validateFile}
        >
          {uploadSection}
        </ImgCrop>
      ) : (
        uploadSection
      )}

      {previewImage && (
        <Image
          wrapperStyle={{
            display: 'none',
          }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(null),
          }}
          src={previewImage}
        />
      )}
    </>
  );
};

export default ImageUpload;
